/**
 * Agentic Payment Airdrop - Shared Utilities
 *
 * Pure functions for data formatting, validation, and transformation.
 * These utilities are stateless and side-effect free.
 */

import { HASHSCAN_BASE_URL } from "./constants";

// ============================================================================
// Date & Time Formatting
// ============================================================================

/**
 * Format a date string to locale string format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or "-" if invalid
 */
export const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString();
};

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * Format a numeric amount with appropriate decimal places
 * @param {string|number} amount - The amount to format
 * @returns {string} Formatted amount string
 */
export const formatAmount = (amount) => {
  if (!amount) return "0";
  return parseFloat(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
};

// ============================================================================
// URL Builders
// ============================================================================

/**
 * Generate HashScan URL for a transaction
 * @param {string} transactionId - Hedera transaction ID
 * @param {string} network - Network name (mainnet/testnet)
 * @returns {string} Full HashScan URL
 */
export const getHashscanUrl = (transactionId, network) => {
  return `${HASHSCAN_BASE_URL}/${network}/transaction/${transactionId}`;
};

// ============================================================================
// Bulk Input Parsing
// ============================================================================

/**
 * Parse bulk text input into array of identifiers
 * Supports newlines, commas, semicolons, and tabs as delimiters
 * @param {string} text - Raw input text
 * @returns {string[]} Array of unique, trimmed identifiers
 */
export const parseBulkInput = (text) => {
  if (!text || !text.trim()) return [];

  const items = text
    .split(/[\n,;\t]+/)
    .map((item) => item.trim().toLowerCase())
    .filter((item) => item.length > 0);

  // Remove duplicates
  return [...new Set(items)];
};

/**
 * Match parsed identifiers against wallet list
 * @param {string[]} identifiers - Array of email/address identifiers
 * @param {Object[]} wallets - Array of wallet objects
 * @returns {Object} Object with matched and unmatched arrays
 */
export const matchWalletsFromInput = (identifiers, wallets) => {
  const matched = [];
  const unmatched = [];

  identifiers.forEach((identifier) => {
    const wallet = wallets.find(
      (w) =>
        w.email?.toLowerCase() === identifier ||
        w.address?.toLowerCase() === identifier,
    );

    if (wallet) {
      matched.push(wallet);
    } else {
      unmatched.push(identifier);
    }
  });

  return { matched, unmatched };
};

// ============================================================================
// Pagination Helpers
// ============================================================================

/**
 * Calculate pagination display range
 * @param {number} currentPage - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {number} showPages - Number of page buttons to show
 * @returns {number[]} Array of page numbers to display
 */
export const calculatePaginationRange = (
  currentPage,
  totalPages,
  showPages = 5,
) => {
  const pages = [];
  let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
  let endPage = Math.min(totalPages, startPage + showPages - 1);

  if (endPage - startPage + 1 < showPages) {
    startPage = Math.max(1, endPage - showPages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return pages;
};

/**
 * Calculate pagination info for display
 * @param {number} currentPage - Current page number
 * @param {number} itemsPerPage - Items per page
 * @param {number} totalItems - Total item count
 * @returns {Object} Start item, end item, and page info
 */
export const getPaginationInfo = (currentPage, itemsPerPage, totalItems) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return {
    startItem,
    endItem,
    totalItems,
  };
};

// ============================================================================
// Text Truncation
// ============================================================================

/**
 * Truncate text with ellipsis if exceeds max length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis or original
 */
export const truncateText = (text, maxLength = 20) => {
  if (!text || text.length <= maxLength) return text || "-";
  return `${text.substring(0, maxLength)}...`;
};

// ============================================================================
// Statistics Calculations
// ============================================================================

/**
 * Calculate history statistics
 * @param {Object[]} history - Array of transfer records
 * @returns {Object} Calculated stats
 */
export const calculateHistoryStats = (history) => {
  const successful = history.filter((h) => h.status === "success").length;
  const failed = history.filter((h) => h.status !== "success").length;
  const totalAmount = history
    .filter((h) => h.status === "success")
    .reduce((sum, h) => sum + parseFloat(h.amount || 0), 0);
  const uniqueTokens = [...new Set(history.map((h) => h.tokenId))].length;

  return {
    successful,
    failed,
    totalAmount,
    uniqueTokens,
    total: history.length,
  };
};

/**
 * Calculate reverse airdrop statistics
 * @param {Object[]} history - Array of reversible transfers
 * @param {number[]} selectedItems - Array of selected item IDs
 * @param {Object[]} filteredHistory - Filtered history array
 * @returns {Object} Calculated stats
 */
export const calculateReverseStats = (
  history,
  selectedItems,
  filteredHistory,
) => {
  const totalReversible = history.length;
  const selectedCount = selectedItems.length;
  const totalAmount = history.reduce(
    (sum, h) => sum + parseFloat(h.amount || 0),
    0,
  );
  const selectedAmount = filteredHistory
    .filter((h) => selectedItems.includes(h.id))
    .reduce((sum, h) => sum + parseFloat(h.amount || 0), 0);
  const uniqueTokens = [...new Set(history.map((h) => h.tokenId))].length;

  return {
    totalReversible,
    selectedCount,
    totalAmount,
    selectedAmount,
    uniqueTokens,
  };
};
